import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth.store';
import { FeatureFlag, type EFeatureFlag } from '@/lib/feature-flags/keys';
import {
  FeatureFlagContext,
  type FeatureFlagContextValue,
} from './FeatureFlagContext';

interface FeatureFlagApiResponse {
  tenantId: string;
  userId: string;
  enabled: Record<EFeatureFlag, boolean>;
  restricted: EFeatureFlag[];
}

const buildDefaultEnabled = (): Record<EFeatureFlag, boolean> => {
  const out = {} as Record<EFeatureFlag, boolean>;
  for (const key of Object.values(FeatureFlag)) {
    out[key] = false;
  }
  return out;
};

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data, isLoading, isError, refetch } = useQuery<FeatureFlagApiResponse>({
    queryKey: ['feature-flags', 'me'],
    queryFn: async () => {
      const { data } = await apiClient.get<FeatureFlagApiResponse>('/system/feature-flags/me');
      return data;
    },
    staleTime: 60_000,
    retry: 1,
    enabled: isAuthenticated,
  });

  const value = useMemo<FeatureFlagContextValue>(() => {
    const base = buildDefaultEnabled();
    if (data?.enabled) {
      for (const key of Object.values(FeatureFlag)) {
        base[key] = data.enabled[key];
      }
    }
    return {
      enabled: base,
      restricted: data?.restricted ?? [],
      isLoading,
      isResolved: !!data?.enabled,
      isError,
      refetch: () => {
        void refetch();
      },
    };
  }, [data, isLoading, isError, refetch]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}
